import React, { useLayoutEffect } from 'react';
import { Link, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, GraduationCap, SearchX } from 'lucide-react';
import { OrganizationApiService, type UserOrganization } from '@/lib/services/organization-api';
import { isOrgSection, orgUrl, parseOrgPath, type OrgCtx } from '@/lib/org-path';
import { isStudentMembership, isWorkspaceMembership, studentEquivalent } from '@/lib/workspace';
import { useUIStore } from '@/store/ui-store';
import { RouteFallback } from '@/routes/lazy';
import { OrgLogo } from '@/components/campus/OrgLogo';

const TYPE: Record<OrgCtx, 'COLLEGE' | 'COMPANY'> = { college: 'COLLEGE', company: 'COMPANY' };

function useMemberships() {
  return useQuery({
    queryKey: ['my-organizations'],
    queryFn: () => OrganizationApiService.getMyOrganizations(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Layout route for /college/:orgSlug/* and /company/:orgSlug/*. The URL decides
 * which organization the workspace shows: the selected-organization state that
 * every page reads is set from the slug here, so a link opens the organization
 * it names and two tabs can be in two organizations. Short-form URLs
 * ("/college/students", from a bookmark or an old link) are resolved to an
 * organization and redirected.
 */
export const OrgScope: React.FC<{ ctx: OrgCtx }> = ({ ctx }) => {
  const { orgSlug = '' } = useParams<{ orgSlug: string }>();
  const { data: memberships, isLoading } = useMemberships();
  const { selectedOrgId, setWorkspaceContext } = useUIStore();

  const shortForm = isOrgSection(ctx, orgSlug);
  const membership = shortForm
    ? undefined
    : memberships?.find(
        (m) =>
          m.organization.slug === orgSlug &&
          m.organization.type === TYPE[ctx] &&
          m.status === 'ACTIVE'
      );
  const usable = !!membership && isWorkspaceMembership(membership);

  useLayoutEffect(() => {
    if (!usable || !membership || selectedOrgId === membership.organization.id) return;
    const o = membership.organization;
    setWorkspaceContext(ctx, {
      id: o.id,
      name: o.name,
      type: o.type,
      logoUrl: o.logoUrl,
      slug: o.slug,
    });
  }, [usable, membership, selectedOrgId, setWorkspaceContext, ctx]);

  if (shortForm) return <ShortFormRedirect ctx={ctx} />;
  if (isLoading || !memberships) return <RouteFallback />;
  if (membership && isStudentMembership(membership)) {
    return <StudentRedirect orgId={membership.organization.id} />;
  }
  if (!usable) return <NotAMember ctx={ctx} />;
  // Hold the page until the selected organization matches the URL, so it never
  // fetches data for the previously selected one.
  if (selectedOrgId !== membership!.organization.id) return <RouteFallback />;
  return <Outlet />;
};

/** A college a student belongs to opens their College Home, not the staff workspace. */
const StudentRedirect: React.FC<{ orgId: string }> = ({ orgId }) => {
  const { pathname } = useLocation();
  const target = studentEquivalent(pathname);
  return (
    <Navigate
      to={target.startsWith('/personal/my-college') ? `${target}?org=${orgId}` : target}
      replace
    />
  );
};

/**
 * "/college/students" with no organization named: use the one this browser last
 * selected, or the only one the person has; with several and no selection, ask.
 */
export const ShortFormRedirect: React.FC<{ ctx: OrgCtx }> = ({ ctx }) => {
  const location = useLocation();
  const { data: memberships, isLoading } = useMemberships();
  const { selectedOrgSlug, selectedOrgType } = useUIStore();

  if (isLoading || !memberships) return <RouteFallback />;
  const options = memberships.filter(
    (m) => m.organization.type === TYPE[ctx] && isWorkspaceMembership(m)
  );
  const page = parseOrgPath(location.pathname)?.rest || '/dashboard';
  const suffix = `${location.search}${location.hash}`;

  const remembered =
    selectedOrgType === TYPE[ctx]
      ? options.find((m) => m.organization.slug === selectedOrgSlug)
      : undefined;
  const pick = remembered ?? (options.length === 1 ? options[0] : undefined);
  if (pick)
    return <Navigate to={`${orgUrl(ctx, pick.organization.slug, page)}${suffix}`} replace />;

  if (options.length === 0) {
    const student = memberships.some((m) => m.status === 'ACTIVE' && isStudentMembership(m));
    return (
      <Navigate
        to={
          ctx === 'college' && student
            ? studentEquivalent(`/college${page}`)
            : '/personal/dashboard'
        }
        replace
      />
    );
  }
  return <ChooseOrganization ctx={ctx} options={options} page={page} suffix={suffix} />;
};

const ChooseOrganization: React.FC<{
  ctx: OrgCtx;
  options: UserOrganization[];
  page: string;
  suffix: string;
}> = ({ ctx, options, page, suffix }) => (
  <div className="wl-page">
    <div style={{ maxWidth: 520, margin: '48px auto', padding: '0 16px' }}>
      <h1 style={{ fontSize: 18, margin: '0 0 6px' }}>
        Which {ctx === 'college' ? 'college' : 'company'} do you want to open?
      </h1>
      <p style={{ fontSize: 13.5, color: 'var(--ink-3)', margin: '0 0 16px' }}>
        You belong to more than one. You can switch any time from the workspace menu.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        {options.map((m) => (
          <li key={m.organization.id}>
            <Link
              to={`${orgUrl(ctx, m.organization.slug, page)}${suffix}`}
              className="wl-card-link"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                borderRadius: 10,
                border: '1px solid var(--line)',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <OrgLogo org={m.organization} size={36} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>{m.organization.name}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const NotAMember: React.FC<{ ctx: OrgCtx }> = ({ ctx }) => {
  const Icon = ctx === 'college' ? GraduationCap : Building2;
  return (
    <div className="wl-page">
      <div className="wl-empty" style={{ padding: '64px 16px', textAlign: 'center' }}>
        <div
          aria-hidden="true"
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: 'var(--bg-2)',
            color: 'var(--ink-4)',
            display: 'grid',
            placeItems: 'center',
            margin: '0 auto 14px',
          }}
        >
          <SearchX size={22} />
        </div>
        <h1 style={{ margin: '0 0 6px', fontSize: 16, color: 'var(--ink)' }}>
          You don't have access to this {ctx === 'college' ? 'college' : 'company'}
        </h1>
        <p style={{ margin: '0 auto 16px', maxWidth: 420, fontSize: 13.5, color: 'var(--ink-3)' }}>
          The link may be for an organization you're not a member of, or your access has ended.
        </p>
        <Link to="/personal/dashboard" className="wl-btn wl-btn-secondary">
          <Icon size={14} aria-hidden="true" /> Go to your dashboard
        </Link>
      </div>
    </div>
  );
};

/** Unknown page inside a real organization. */
export const OrgPageNotFound: React.FC<{ ctx: OrgCtx }> = ({ ctx }) => (
  <div className="wl-page">
    <div className="wl-empty" style={{ padding: '64px 16px', textAlign: 'center' }}>
      <h1 style={{ margin: '0 0 6px', fontSize: 16, color: 'var(--ink)' }}>Page not found</h1>
      {/* Short form: the router keeps this tab's organization. */}
      <Link to={`/${ctx}/dashboard`} className="wl-btn wl-btn-secondary">
        Go to the dashboard
      </Link>
    </div>
  </div>
);
